<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="96%"
    top="2vh"
    custom-class="agreement-editor-dialog"
    append-to-body
    :close-on-click-modal="false"
    @closed="onClosed"
  >
    <p class="agreement-editor-hint">
      Saving always creates a <strong>new version</strong> as a new file. Existing versions are never overwritten or deleted.
    </p>

    <div v-if="loadError" class="agreement-editor-load-error">
      <el-alert type="error" :closable="false" :title="loadError" show-icon />
    </div>

    <div class="agreement-editor-layout" v-loading="loadingContent">
      <div class="agreement-editor-pane agreement-editor-pane--source">
        <div class="pane-label">Document</div>
        <div class="agreement-editor-main" v-loading="placeholdersLoading">
          <p class="tab-help">Edit the agreement directly like a document.</p>

          <details v-if="availablePlaceholdersList.length" class="insert-toolbar" :open="!isMobile">
            <summary class="insert-toolbar-label">Insert token (text)</summary>
            <div class="insert-toolbar-buttons">
              <el-button
                v-for="(ph, idx) in availablePlaceholdersList"
                :key="'ins-' + idx"
                size="mini"
                type="default"
                class="insert-ph-btn insert-ph-btn--pin"
                @click="insertPlaceholder(ph)"
              >
                {{ ph }}
              </el-button>
            </div>
            <span class="insert-toolbar-note">Tokens are saved as plain text placeholders in the document.</span>
          </details>

          <editor ref="tinymceEditor" :key="tinymceKey" v-model="editorHtml" :init="tinymceInit" />
        </div>

        <details class="meta-panel">
          <summary>Details and placeholders</summary>
          <el-form label-position="top" class="agreement-editor-desc">
            <el-form-item label="Description (optional)">
              <el-input
                v-model="description"
                type="textarea"
                :rows="2"
                placeholder="e.g. Q1 2026 fee schedule update"
              />
            </el-form-item>
          </el-form>

          <div v-if="availablePlaceholdersList.length" class="placeholder-panel">
            <div class="placeholder-panel-title">Registered placeholders (reference)</div>
            <div class="placeholder-chips">
              <code
                v-for="(ph, idx) in availablePlaceholdersList"
                :key="'ph-' + idx"
                class="ph-chip ph-chip--clickable"
                title="Click to insert token"
                @click="insertPlaceholder(ph)"
              >
                {{ ph }}
              </code>
            </div>
            <div v-if="editorHtml && missingRegisteredInFile.length" class="missing-ph">
              <span class="missing-ph-label">Not found in document yet:</span>
              <code v-for="(ph, i) in missingRegisteredInFile" :key="'m-' + i" class="ph-missing">{{ ph }}</code>
            </div>
          </div>
        </details>
      </div>

      <div class="agreement-editor-pane agreement-editor-pane--preview" :class="{ 'mobile-hidden': !mobilePreviewVisible }">
        <div class="pane-label pane-label--preview">
          Preview (live, sync scroll)
          <el-button class="mobile-preview-close" size="mini" type="text" icon="el-icon-close" @click="mobilePreviewVisible = false" />
        </div>
        <iframe
          ref="previewIframe"
          class="agreement-preview-iframe"
          :srcdoc="previewSrcdoc"
          sandbox="allow-same-origin"
          @load="onPreviewIframeLoad"
        />
      </div>

      <el-button class="mobile-preview-toggle" size="small" icon="el-icon-view" @click="mobilePreviewVisible = true">
        Show preview
      </el-button>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">Cancel</el-button>
      <el-button
        type="primary"
        :loading="saving"
        :disabled="!canSave"
        icon="el-icon-document-add"
        @click="handleSave"
      >
        Save as new version
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { Dialog, Button, Input, Form, FormItem, Alert, Message, MessageBox } from 'element-ui';
import Editor from '@tinymce/tinymce-vue';
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/paste';
import 'tinymce/plugins/table';
import 'tinymce/skins/ui/oxide/skin.min.css';
// import 'tinymce/skins/ui/oxide/content.min.css';
// import 'tinymce/skins/content/default/content.min.css';

export default {
  name: 'AgreementEditorModal',
  components: {
    editor: Editor,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
    [Input.name]: Input,
    [Form.name]: Form,
    [FormItem.name]: FormItem,
    [Alert.name]: Alert,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    agreementType: {
      type: String,
      default: '',
    },
    cloneFromId: {
      default: null,
      validator: (v) => v === null || v === undefined || Number.isInteger(v),
    },
  },
  data() {
    return {
      editorHtml: '',
      description: '',
      previewSrcdoc: '',
      mobilePreviewVisible: false,
      previewTimer: null,
      syncInProgress: false,
      iframeScrollBound: false,
      loadingContent: false,
      loadError: '',
      saving: false,
      placeholdersLoading: false,
      legacyHeadStyles: '',
      tinymceKey: 0,
      tinymceInit: {
        menubar: false,
        branding: false,
        statusbar: true,
        height: '100%',
        skin: false,
        content_css: false,
        plugins: 'link lists table paste',
        toolbar:
          'undo redo | formatselect | bold italic underline | alignleft aligncenter alignright | bullist numlist | table | link | removeformat',
        content_style:
          'html,body{max-width:100%;overflow-x:hidden;} body{margin:12px;font-family:Arial,Helvetica,sans-serif;color:#222;line-height:1.45;font-size:14px;word-break:break-word;overflow-wrap:anywhere;} h1,h2,h3,h4,h5,h6{font-size:1em;line-height:1.45;margin:0 0 10px;font-weight:700;} *{max-width:100%;box-sizing:border-box;} table{border-collapse:collapse;width:100% !important;table-layout:fixed;} td,th{vertical-align:top;word-break:break-word;overflow-wrap:anywhere;} img{max-width:100% !important;height:auto !important;}',
        paste_data_images: false,
        setup: (editor) => {
          editor.on('init', () => this.syncPreviewFromEditor());
          editor.on('ScrollContent', () => this.syncPreviewFromEditor());
        },
      },
    };
  },
  computed: {
    ...mapGetters(['registeredPlaceholders']),
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      },
    },
    dialogTitle() {
      const map = {
        minimum_spend_v4: 'Agency Minimum Spend (v4)',
        default_agency: 'Agency',
        default_client: 'Client',
      };
      const t = map[this.agreementType] || this.agreementType;
      return t ? `New version - ${t}` : 'New agreement version';
    },
    availablePlaceholdersList() {
      const list = this.registeredPlaceholders[this.agreementType];
      if (!Array.isArray(list) || !list.length) return [];
      return [].concat(list).sort((a, b) => a.localeCompare(b));
    },
    missingRegisteredInFile() {
      if (!this.editorHtml || !this.availablePlaceholdersList.length) return [];
      return this.availablePlaceholdersList.filter((ph) => this.editorHtml.indexOf(ph) === -1);
    },
    isMobile() {
      return typeof window !== 'undefined' && window.innerWidth <= 768;
    },
    canSave() {
      return (this.editorHtml || '').trim().length > 0 && !!this.agreementType;
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.tinymceKey += 1;
        this.bootstrap();
      } else {
        this.unbindIframeScrollSync();
      }
    },
    editorHtml() {
      this.schedulePreview();
    },
  },
  methods: {
    ...mapActions([
      'getRegisteredPlaceholders',
      'fetchAgreementHtmlContent',
      'uploadAgreementFromHtml',
      'activateAgreement',
    ]),
    insertPlaceholder(token) {
      const inst = this.$refs.tinymceEditor && this.$refs.tinymceEditor.editor;
      if (inst && typeof inst.insertContent === 'function') {
        inst.focus();
        inst.insertContent(token);
      }
    },
    schedulePreview() {
      if (this.previewTimer) clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(() => {
        this.previewSrcdoc = this.buildPreviewSrcdoc(this.editorHtml || '');
      }, 180);
    },
    getIframeScrollingEl() {
      const iframe = this.$refs.previewIframe;
      if (!iframe) return null;
      try {
        const doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
        if (!doc) return null;
        return doc.scrollingElement || doc.documentElement || doc.body;
      } catch (_) {
        return null;
      }
    },
    onPreviewIframeLoad() {
      this.bindIframeScrollSync();
    },
    bindIframeScrollSync() {
      this.unbindIframeScrollSync();
      const iframe = this.$refs.previewIframe;
      if (!iframe) return;
      try {
        const doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
        if (!doc) return;
        this._iframeScrollHandler = () => this.syncEditorFromPreview();
        doc.addEventListener('scroll', this._iframeScrollHandler, { passive: true });
        this.iframeScrollBound = true;
      } catch (_) {
        /* cross-origin guard */
      }
    },
    unbindIframeScrollSync() {
      if (!this.iframeScrollBound) return;
      try {
        const iframe = this.$refs.previewIframe;
        if (iframe) {
          const doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
          if (doc && this._iframeScrollHandler) {
            doc.removeEventListener('scroll', this._iframeScrollHandler);
          }
        }
      } catch (_) { /* ignore */ }
      this._iframeScrollHandler = null;
      this.iframeScrollBound = false;
    },
    getEditorScroller() {
      const inst = this.$refs.tinymceEditor && this.$refs.tinymceEditor.editor;
      if (!inst || !inst.getDoc) return null;
      const doc = inst.getDoc();
      if (!doc) return null;
      return doc.scrollingElement || doc.documentElement || doc.body;
    },
    syncPreviewFromEditor() {
      const from = this.getEditorScroller();
      const to = this.getIframeScrollingEl();
      if (!from || !to || this.syncInProgress) return;
      this.syncInProgress = true;
      const fromMax = Math.max((from.scrollHeight || 0) - (from.clientHeight || 0), 1);
      const ratio = (from.scrollTop || 0) / fromMax;
      const toMax = Math.max((to.scrollHeight || 0) - (to.clientHeight || 0), 0);
      to.scrollTop = ratio * toMax;
      window.setTimeout(() => { this.syncInProgress = false; }, 0);
    },
    syncEditorFromPreview() {
      const to = this.getEditorScroller();
      const from = this.getIframeScrollingEl();
      if (!from || !to || this.syncInProgress) return;
      this.syncInProgress = true;
      const fromMax = Math.max((from.scrollHeight || 0) - (from.clientHeight || 0), 1);
      const ratio = (from.scrollTop || 0) / fromMax;
      const toMax = Math.max((to.scrollHeight || 0) - (to.clientHeight || 0), 0);
      to.scrollTop = ratio * toMax;
      window.setTimeout(() => { this.syncInProgress = false; }, 0);
    },
    extractHeadStyles(rawHtml) {
      const html = String(rawHtml || '');
      const styles = [];
      const styleRegex = /<style\b[^>]*>[\s\S]*?<\/style>/gi;
      let match = styleRegex.exec(html);
      while (match) {
        styles.push(match[0]);
        match = styleRegex.exec(html);
      }
      return styles.join('\n');
    },
    normalizeLegacyHtml(rawHtml) {
      let html = String(rawHtml || '');
      html = html.replace(/^\uFEFF/, '');
      html = html.replace(/<\?xml[^>]*\?>/gi, '');
      html = html.replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, '');
      html = html.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '');
      html = html.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '');
      html = html.replace(/\s+on[a-z]+\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, '');
      const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      const body = bodyMatch ? bodyMatch[1] : html;
      return body
        .replace(/\u00A0/g, ' ')
        .replace(/[ \t]+/g, ' ')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    },
    buildPreviewSrcdoc(source) {
      let body = String(source || '').trim();
      if (!body) return '';
      const m = body.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      if (m) body = m[1];
      body = body.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '');
      body = body.replace(/\s+on[a-z]+\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, '');
      const baseStyle = `<style>body{margin:12px;font-family:Arial,Helvetica,sans-serif;color:#222;line-height:1.45;font-size:14px;word-break:break-word;overflow-wrap:anywhere;}table{border-collapse:collapse;width:100%;}td,th{vertical-align:top;word-break:break-word;overflow-wrap:anywhere;}img{max-width:100%!important;height:auto!important;}</style>`;
      return `<!doctype html><html><head><meta charset="UTF-8">${this.legacyHeadStyles || ''}${baseStyle}</head><body>${body}</body></html>`;
    },
    sanitizeBodyForSave(source) {
      let body = String(source || '').trim();
      if (!body) return '';
      const m = body.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      if (m) body = m[1];
      body = body.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '');
      body = body.replace(/\s+on[a-z]+\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, '');
      return body;
    },
    wrapFreshHtml(bodyHtml, extraStyles = '') {
      return `<!doctype html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body { font-family: Arial, Helvetica, sans-serif; color: #222; line-height: 1.45; }
    table { border-collapse: collapse; width: 100%; }
    td, th { vertical-align: top; }
  </style>
  ${extraStyles || ''}
</head>
<body>
${bodyHtml || ''}
</body>
</html>`;
    },
    async bootstrap() {
      this.loadError = '';
      this.editorHtml = '';
      this.description = '';
      this.previewSrcdoc = '';
      this.legacyHeadStyles = '';
      if (!this.agreementType) {
        this.loadError = 'No agreement type selected.';
        return;
      }
      this.placeholdersLoading = true;
      try {
        await this.getRegisteredPlaceholders(this.agreementType);
      } catch (e) {
        console.error(e);
      } finally {
        this.placeholdersLoading = false;
      }
      if (this.cloneFromId == null) return;
      this.loadingContent = true;
      try {
        const html = await this.fetchAgreementHtmlContent(this.cloneFromId);
        this.legacyHeadStyles = this.extractHeadStyles(html);
        this.editorHtml = this.normalizeLegacyHtml(html);
      } catch (e) {
        this.loadError = 'Could not load agreement HTML. You can still paste content manually.';
      } finally {
        this.loadingContent = false;
      }
    },
    onClosed() {
      this.unbindIframeScrollSync();
      this.editorHtml = '';
      this.description = '';
      this.previewSrcdoc = '';
      this.loadError = '';
      this.legacyHeadStyles = '';
      if (this.previewTimer) {
        clearTimeout(this.previewTimer);
        this.previewTimer = null;
      }
      this.$emit('closed');
    },
    async handleSave() {
      const body = this.sanitizeBodyForSave(this.editorHtml || '');
      if (!body || !this.agreementType) return;
      this.saving = true;
      try {
        const saved = await this.uploadAgreementFromHtml({
          agreement_type: this.agreementType,
          html_content: this.wrapFreshHtml(body, this.legacyHeadStyles),
          description: this.description || '',
          source_mode: this.cloneFromId != null ? 'clone_existing' : 'manual',
        });

        let newAgreementId = null;
        if (saved && saved.data && Number.isInteger(saved.data.id)) {
          newAgreementId = saved.data.id;
        }

        if (newAgreementId) {
          try {
            await MessageBox.confirm(
              'New version saved successfully. Do you want to activate it now?',
              'Activate new version',
              {
                confirmButtonText: 'Activate now',
                cancelButtonText: 'Keep inactive',
                type: 'warning',
                distinguishCancelAndClose: true,
              }
            );

            await this.activateAgreement(newAgreementId);
            Message.success('New version saved and activated.');
          } catch (confirmOrActivateError) {
            if (confirmOrActivateError === 'cancel' || confirmOrActivateError === 'close') {
              Message.info('New version saved as inactive.');
            } else {
              Message.error('New version saved, but failed to activate it.');
            }
          }
        } else {
          Message.success('New version saved (inactive). Activate it when ready.');
        }

        this.dialogVisible = false;
        this.$emit('saved');
      } catch (err) {
        const is413 = (err && err.message && err.message.indexOf('413') !== -1)
          || (err && err.response && err.response.status === 413);
        if (is413) {
          Message.error('The document is too large to save. Please reduce the content length or remove unnecessary formatting, then try again.');
        } else {
          const msg = err && err.message ? err.message : 'Failed to save';
          Message.error(msg);
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.agreement-editor-hint { margin: 0 0 12px; font-size: 13px; color: #606266; line-height: 1.5; }
.agreement-editor-load-error { margin-bottom: 12px; }
.agreement-editor-layout { display: flex; flex-wrap: nowrap; gap: 16px; height: calc(100vh - 260px); min-height: 480px; overflow: hidden; position: relative; }
.agreement-editor-pane { flex: 1 1 0; min-width: 280px; min-height: 0; display: flex; flex-direction: column; overflow: hidden; }
.pane-label { font-size: 12px; font-weight: 600; color: #909399; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 8px; }
.agreement-editor-main { width: 100%; flex: 1; min-height: 0; display: flex; flex-direction: column; overflow: visible; }
.agreement-editor-main >>> .tox-tinymce { flex: 1; min-height: 0; border-radius: 4px; max-width: 100%; overflow: hidden; }
.tab-help { margin: 0 0 10px; font-size: 13px; color: #606266; line-height: 1.5; flex-shrink: 0; }
.insert-toolbar { margin-bottom: 10px; padding: 8px 10px; background: #f5f7fa; border-radius: 4px; border: 1px solid #ebeef5; flex-shrink: 0; }
.insert-toolbar > summary { cursor: pointer; font-size: 12px; font-weight: 600; color: #606266; outline: none; list-style: revert; }
.insert-toolbar[open] > summary { margin-bottom: 8px; }
.insert-toolbar-buttons { display: flex; flex-wrap: wrap; gap: 6px; max-height: 120px; overflow-y: auto; }
.insert-ph-btn { font-family: Consolas, Monaco, 'Courier New', monospace; font-size: 11px !important; padding: 5px 8px !important; white-space: normal; height: auto !important; line-height: 1.3 !important; text-align: left; }
.insert-ph-btn--pin { border-radius: 999px !important; }
.insert-ph-btn--pin::before { content: '# '; opacity: 0.7; }
.insert-toolbar-note { display: block; margin-top: 8px; font-size: 11px; color: #909399; }
.agreement-editor-desc { margin-top: 12px; flex-shrink: 0; }
.agreement-preview-iframe { width: 100%; height: 100%; min-height: 0; border: 1px solid #dcdfe6; border-radius: 4px; background: #fff; }
.meta-panel { margin-top: 10px; border: 1px solid #ebeef5; border-radius: 4px; background: #fafafa; padding: 8px 10px; flex-shrink: 0; }
.meta-panel > summary { cursor: pointer; font-size: 12px; color: #606266; font-weight: 600; outline: none; }
.meta-panel[open] > summary { margin-bottom: 8px; }
.placeholder-panel { margin-top: 12px; padding: 10px; background: #f5f7fa; border-radius: 4px; max-height: 140px; overflow-y: auto; flex-shrink: 0; }
.placeholder-panel-title { font-size: 12px; font-weight: 600; color: #606266; margin-bottom: 8px; }
.placeholder-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.ph-chip { font-size: 11px; background: #fff; padding: 2px 6px; border-radius: 3px; border: 1px solid #e4e7ed; }
.ph-chip--clickable { cursor: pointer; border-radius: 999px; }
.ph-chip--clickable:hover { border-color: #b8d8ff; background: #eef6ff; }
.missing-ph { margin-top: 8px; font-size: 12px; color: #e6a23c; }
.missing-ph-label { display: block; margin-bottom: 4px; }
.ph-missing { display: inline-block; margin-right: 6px; margin-bottom: 4px; font-size: 11px; background: #fdf6ec; padding: 2px 6px; border-radius: 3px; }
.mobile-preview-toggle { display: none; }
.mobile-preview-close { display: none !important; }
.pane-label--preview { display: flex; align-items: center; justify-content: space-between; }

@media (max-width: 768px) {
  .agreement-editor-hint {
    margin: 0 0 6px;
    font-size: 12px;
  }
  .agreement-editor-layout {
    flex: 1;
    display: flex;
    flex-direction: column;
    height: auto;
    min-height: 0;
    overflow: hidden;
  }
  .agreement-editor-pane {
    min-width: 0;
    flex: none;
  }
  .agreement-editor-pane--source {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .agreement-editor-main {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .agreement-editor-main >>> .tox-tinymce {
    flex: 1;
    min-height: 120px;
  }
  .agreement-editor-pane--preview {
    position: fixed;
    top: 4vh;
    left: 3vw;
    right: 3vw;
    bottom: 4vh;
    z-index: 99999;
    background: #fff;
    padding: 12px;
    margin: 0;
    max-height: none;
    min-height: 0;
    border-radius: 10px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  }
  .agreement-editor-pane--preview.mobile-hidden {
    display: none;
  }
  .agreement-editor-pane--preview .pane-label--preview {
    flex-shrink: 0;
    margin-bottom: 8px;
  }
  .agreement-preview-iframe {
    height: calc(100% - 40px);
  }
  .mobile-preview-toggle {
    display: block;
    margin-top: 8px;
    width: 100%;
    flex-shrink: 0;
  }
  .mobile-preview-close {
    display: inline-flex !important;
    padding: 4px !important;
    font-size: 20px !important;
    color: #606266 !important;
  }
  .insert-toolbar-buttons {
    max-height: 80px;
  }
  .insert-ph-btn {
    font-size: 10px !important;
    padding: 3px 6px !important;
  }
  .tab-help {
    display: none;
  }
  .meta-panel {
    margin-top: 6px;
    flex-shrink: 0;
  }
}
</style>

<style>
.agreement-editor-dialog { margin-top: 2vh !important; }
.agreement-editor-dialog .el-dialog { height: 96vh; display: flex; flex-direction: column; }
.agreement-editor-dialog .el-dialog__body { padding-top: 10px; flex: 1; min-height: 0; overflow: hidden; display: flex; flex-direction: column; }
.agreement-editor-dialog .el-dialog__footer { flex-shrink: 0; }

@media (max-width: 768px) {
  .agreement-editor-dialog { margin-top: 0 !important; }
  .agreement-editor-dialog .el-dialog {
    height: 100vh;
    height: 100dvh;
    width: 100% !important;
    margin: 0 !important;
    border-radius: 0;
  }
  .agreement-editor-dialog .el-dialog__body {
    overflow: hidden;
    padding: 10px 12px;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .agreement-editor-dialog .el-dialog__header { padding: 10px 12px; flex-shrink: 0; }
  .agreement-editor-dialog .el-dialog__footer {
    flex-shrink: 0;
    padding: 8px 12px;
    background: #fff;
    border-top: 1px solid #ebeef5;
  }
}
.tox-tinymce-aux,
.tox-silver-sink,
.tox .tox-menu {
  z-index: 100000 !important;
}

@media (max-width: 768px) {
  .agreement-editor-dialog .tox .tox-toolbar__group {
    flex-wrap: wrap;
  }
  .agreement-editor-dialog .tox .tox-editor-header {
    max-width: 100%;
    overflow-x: hidden;
  }
}
</style>
